import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getTicketsSummary, listMyTickets } from '../api/tickets';
import type { Ticket, TicketSummary } from '../types/ticket';
import EmptyState from '../components/EmptyState';
import StatusBadge from '../components/StatusBadge';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { formatDate } from '../utils/formatDate';

type TabKey = 'active' | 'resolved' | 'all';

function statusFilter(t: TabKey): string | undefined {
  if (t === 'active') return 'open,in_progress';
  if (t === 'resolved') return 'resolved,closed';
  return undefined;
}

const TAB_LABEL_KEYS: Record<TabKey, string> = {
  active: 'myTickets.tabActive',
  resolved: 'myTickets.tabResolved',
  all: 'myTickets.tabAll',
};

interface StatProps {
  label: string;
  n: number;
  highlight?: boolean;
}

function Stat({ label, n, highlight }: StatProps) {
  return (
    <div
      className={`ticket-stat-card${highlight ? ' ticket-stat-card-highlight' : ''}`}
    >
      <div className="ticket-stat-label">
        {label}
      </div>
      <div className={`ticket-stat-value${highlight && n > 0 ? ' ticket-stat-value-warning' : ''}`}>
        {n}
      </div>
    </div>
  );
}

export default function MyTickets() {
  const { t } = useTranslation('tickets');
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>('active');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [summary, setSummary] = useState<TicketSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const [sum, list] = await Promise.all([
          getTicketsSummary(),
          listMyTickets(statusFilter(tab), 1, 50),
        ]);
        if (ignore) return;
        setSummary(sum);
        setTickets(list.data);
      } catch (e) {
        if (!ignore) toast.error(t('myTickets.loadFailed'));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [tab, toast, t]);

  return (
    <div className="container tickets-page">
      <Breadcrumb items={[{ label: t('myTickets.breadcrumbHome'), to: '/projects' }, { label: t('myTickets.breadcrumbTitle') }]} />
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('myTickets.pageTitle')}</h1>
          <p className="tickets-page-subtitle">{t('myTickets.pageSubtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/tickets/new')}>
          {t('myTickets.newTicket')}
        </button>
      </div>

      {summary && (
        <div className="ticket-stats-grid">
          <Stat label={t('myTickets.statPending')} n={summary.open} />
          <Stat label={t('myTickets.statInProgress')} n={summary.inProgress} />
          <Stat label={t('myTickets.statAwaitingReply')} n={summary.awaitingUserReply} highlight />
        </div>
      )}

      <div className="ticket-tabs" role="tablist" aria-label={t('myTickets.breadcrumbTitle')}>
        {(['active', 'resolved', 'all'] as TabKey[]).map((k) => {
          const active = tab === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`ticket-tab${active ? ' ticket-tab-active' : ''}`}
              role="tab"
              aria-selected={active}
            >
              {t(TAB_LABEL_KEYS[k])}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="tickets-loading">{t('myTickets.loading')}</div>
      ) : tickets.length === 0 ? (
        <EmptyState icon="📮" description={t('myTickets.emptyDesc')} />
      ) : (
        <div className="ticket-list">
          {tickets.map((tk) => (
            <Link
              to={`/tickets/${tk.id}`}
              key={tk.id}
              className="ticket-list-card"
            >
              <div className="ticket-list-card-main">
                <strong className="ticket-list-card-title">{tk.title}</strong>
                <StatusBadge
                  status={tk.status}
                  label={t(`status.${tk.status}`, { defaultValue: tk.status })}
                />
              </div>
              <div className="ticket-list-meta">
                <span>{t(`category.${tk.category}`, { defaultValue: tk.category })}</span>
                {tk.projectName && <span>{tk.projectName}</span>}
                {tk.relatedName && <span>{tk.relatedName}</span>}
                <span>{formatDate(tk.lastActivityAt)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
